import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  List,
  Scissors,
  Calculator,
  ChefHat,
  Lightbulb,
  AlertTriangle,
} from 'lucide-react';

/* ─── Preset ingredients with typical merma % ────────── */

const PRESETS = [
  { label: 'Selecciona ingrediente...', merma: 0 },
  { label: 'Patata', merma: 15 },
  { label: 'Cebolla', merma: 10 },
  { label: 'Zanahoria', merma: 18 },
  { label: 'Pimiento', merma: 18 },
  { label: 'Tomate', merma: 5 },
  { label: 'Brocoli', merma: 35 },
  { label: 'Alcachofa', merma: 55 },
  { label: 'Puerro', merma: 35 },
  { label: 'Pollo entero', merma: 25 },
  { label: 'Ternera (pieza)', merma: 18 },
  { label: 'Cordero (pierna)', merma: 20 },
  { label: 'Cerdo (lomo)', merma: 10 },
  { label: 'Merluza entera', merma: 45 },
  { label: 'Lubina entera', merma: 40 },
  { label: 'Rape entero', merma: 55 },
  { label: 'Salmon (lomo)', merma: 5 },
  { label: 'Langostino', merma: 30 },
  { label: 'Gamba roja', merma: 35 },
  { label: 'Cigala', merma: 55 },
  { label: 'Mejillon', merma: 60 },
  { label: 'Pulpo', merma: 30 },
];

/* ─── Top 15 mermas rapidas ──────────────────────────── */

const MERMAS_RAPIDAS = [
  ['Alcachofa', '55%', '2,22', 'Hojas externas, tallo, pelusa'],
  ['Rape entero', '55%', '2,22', 'Cabeza enorme, piel, espinas'],
  ['Mejillon', '60%', '2,50', 'Conchas (peso elevado)'],
  ['Cigala', '55%', '2,22', 'Cabeza, cascara, pinzas'],
  ['Brocoli', '35%', '1,54', 'Tronco, hojas exteriores'],
  ['Puerro', '35%', '1,54', 'Hojas verdes exteriores'],
  ['Gamba roja', '35%', '1,54', 'Cabeza, cascara, intestino'],
  ['Langostino', '30%', '1,43', 'Cabeza, cascara'],
  ['Pulpo', '30%', '1,43', 'Coccion (pierde agua y peso)'],
  ['Pollo entero', '25%', '1,33', 'Huesos, piel, menudillos'],
  ['Cordero (pierna)', '20%', '1,25', 'Hueso, grasa, recortes'],
  ['Zanahoria', '18%', '1,22', 'Piel, extremos'],
  ['Patata', '15%', '1,18', 'Piel, ojos, zonas verdes'],
  ['Cebolla', '10%', '1,11', 'Piel seca, raiz'],
  ['Salmon (lomo)', '5%', '1,05', 'Piel, espinas laterales'],
];

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Que es la merma de un ingrediente en cocina?',
    a: 'La merma es el porcentaje de peso que se pierde al manipular un ingrediente desde su estado bruto (tal como lo recibes del proveedor) hasta que esta listo para cocinar o servir. Incluye la limpieza (pelado, deshuesado, despinado) y la coccion (perdida de agua, grasa). Un ingrediente con 40% de merma significa que de cada kilo comprado solo aprovechas 600 gramos.',
  },
  {
    q: '¿Como se calcula el precio real de un ingrediente con merma?',
    a: 'La formula es: Precio real = Precio de compra / (1 - Merma%). Por ejemplo, si compras rape entero a 18 euros/kg y tiene un 55% de merma, el precio real del producto utilizable es 18 / (1 - 0,55) = 40 euros/kg. Esa es la cifra que debes usar en tu escandallo para conocer el coste real de tus platos.',
  },
  {
    q: '¿Que es el factor de rendimiento y para que sirve?',
    a: 'El factor de rendimiento es el multiplicador que aplicas al peso neto que necesitas para saber cuanto producto bruto debes comprar. Se calcula como: Factor = 1 / (1 - Merma%). Si necesitas 500 g de alcachofa limpia y su factor es 2,22, debes comprar 500 x 2,22 = 1.110 g de alcachofas brutas.',
  },
  {
    q: '¿Las mermas preestablecidas de la calculadora son exactas?',
    a: 'Son valores orientativos basados en promedios de cocina profesional espanola. Las mermas reales varian segun la calidad del proveedor, la temporada, el tamano de las piezas y la habilidad del equipo de cocina. Usa estos datos como punto de partida y ajusta con tus propias mediciones.',
  },
  {
    q: '¿Debo aplicar merma a ingredientes secos como arroz o especias?',
    a: 'Los ingredientes secos (especias, sal, azucar, harina, arroz, pasta seca) tienen merma de limpieza practicamente nula (0%). Algunos ganan peso en coccion (el arroz triplica su peso al absorber agua). Para el escandallo, lo habitual es calcular el coste sobre el peso seco de compra y no aplicar merma.',
  },
  {
    q: '¿Esta calculadora de mermas es gratuita?',
    a: 'Si, esta calculadora es 100% gratuita y no requiere registro. Para funcionalidades avanzadas como mermas automaticas en todos tus escandallos, OCR de facturas, precios de mercado en tiempo real y control de food cost para toda la carta, puedes probar PLEKO gratis durante 14 dias.',
  },
];

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'calculadora', label: 'Calculadora de mermas' },
  { id: 'como-usar', label: 'Como usar esta calculadora' },
  { id: 'formula-merma', label: 'La formula de la merma' },
  { id: 'tabla-rapida', label: 'Tabla rapida: mermas mas comunes' },
  { id: 'ejemplo-langostinos', label: 'Por que la merma cambia el coste real' },
  { id: 'tipos-merma', label: 'Tipos de merma' },
  { id: 'reducir-mermas', label: 'Como reducir mermas en tu cocina' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const webAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculadora de Mermas en Cocina',
  url: 'https://pleko.app/calculadora-mermas',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'All',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  author: { '@type': 'Organization', name: 'PLEKO' },
  description:
    'Calculadora de mermas gratuita para restaurantes. Calcula el coste real de tus ingredientes aplicando el porcentaje de merma, el factor de rendimiento y la cantidad bruta necesaria.',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Mermas en Cocina', item: 'https://pleko.app/mermas-cocina' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Calculadora de Mermas',
      item: 'https://pleko.app/calculadora-mermas',
    },
  ],
};

/* ─── Helpers ────────────────────────────────────────── */

function p(v: string): number {
  return parseFloat(v.replace(',', '.'));
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals).replace('.', ',');
}

/* ─── Component ──────────────────────────────────────── */

export default function CalculadoraMermasPage() {
  const [pesoBruto, setPesoBruto] = useState('1000');
  const [unidad, setUnidad] = useState<'g' | 'kg'>('g');
  const [merma, setMerma] = useState(15);
  const [precioCompra, setPrecioCompra] = useState('4.50');
  const [pesoNetoDeseado, setPesoNetoDeseado] = useState('500');
  const [selectedPreset, setSelectedPreset] = useState(0);

  /* ── Calculations ── */
  const pesoBrutoNum = p(pesoBruto) || 0;
  const precioNum = p(precioCompra) || 0;
  const pesoNetoDeseadoNum = p(pesoNetoDeseado) || 0;

  // Convert to grams for calculation
  const pesoBrutoG = unidad === 'kg' ? pesoBrutoNum * 1000 : pesoBrutoNum;

  const pesoNeto = pesoBrutoG * (1 - merma / 100);
  const factorRendimiento = merma < 100 ? 1 / (1 - merma / 100) : 0;
  const precioRealKg = merma < 100 ? precioNum / (1 - merma / 100) : 0;
  const cantidadBrutaNecesaria = pesoNetoDeseadoNum * factorRendimiento;

  const handlePresetChange = (index: number) => {
    setSelectedPreset(index);
    if (index > 0) {
      setMerma(PRESETS[index].merma);
    }
  };

  /* ── Render ── */
  return (
    <>
      <Helmet>
        <title>Calculadora de Mermas en Cocina — Calcula el Coste Real de tus Ingredientes | PLEKO</title>
        <meta
          name="description"
          content="Calculadora de mermas gratuita para restaurantes. Calcula el coste real de tus ingredientes con merma, factor de rendimiento y cantidad bruta necesaria. Con presets de ingredientes comunes. Sin registro."
        />
        <meta
          name="keywords"
          content="calculadora mermas cocina, calcular merma alimentos, calculadora rendimiento ingredientes, merma cocina calculadora, calcular coste real ingrediente, factor rendimiento alimentos, merma ingredientes calculadora"
        />
        <link rel="canonical" href="https://pleko.app/calculadora-mermas" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pleko.app/calculadora-mermas" />
        <meta
          property="og:title"
          content="Calculadora de Mermas en Cocina — Calcula el Coste Real de tus Ingredientes | PLEKO"
        />
        <meta
          property="og:description"
          content="Calcula el coste real de tus ingredientes aplicando la merma. Factor de rendimiento, precio real por kg, cantidad bruta necesaria. Gratis y sin registro."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Calculadora de Mermas en Cocina | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Calcula el coste real de tus ingredientes aplicando la merma. Factor de rendimiento, precio real por kg, cantidad bruta necesaria."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-emerald-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Calculadora online gratuita — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Calculadora de <span className="text-emerald-600">Mermas</span> en Cocina — Calcula el Coste Real de tus Ingredientes
              </h1>
              <p className="text-xl text-slate-600 mb-4 max-w-2xl mx-auto">
                Introduce el peso bruto, el porcentaje de merma y el precio de compra para obtener al instante el peso neto, el coste real por kg, el factor de rendimiento y la cantidad bruta que necesitas comprar.
              </p>
              <p className="text-base text-slate-500 max-w-xl mx-auto">
                Con presets para mas de 20 ingredientes comunes. Gratis, sin registro.
              </p>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta pagina</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </section>

        {/* ═══════════ CALCULATOR ═══════════ */}
        <section id="calculadora" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20">
          <div className="border-2 border-emerald-200 rounded-2xl p-6 sm:p-8 mb-8 bg-emerald-50/30">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Scissors className="w-6 h-6 text-emerald-600" />
              Calculadora de mermas
            </h2>

            {/* Preset selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1">Ingrediente con merma predefinida</label>
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(Number(e.target.value))}
                className="w-full sm:w-80 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              >
                {PRESETS.map((preset, i) => (
                  <option key={i} value={i}>
                    {preset.label}{i > 0 ? ` (${preset.merma}%)` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Inputs grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Peso bruto</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="1000"
                    value={pesoBruto}
                    onChange={(e) => setPesoBruto(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <select
                    value={unidad}
                    onChange={(e) => setUnidad(e.target.value as 'g' | 'kg')}
                    className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Merma: <strong className="text-emerald-700">{merma}%</strong>
                </label>
                <input
                  type="range"
                  min="0"
                  max="70"
                  step="1"
                  value={merma}
                  onChange={(e) => { setMerma(Number(e.target.value)); setSelectedPreset(0); }}
                  className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 mt-2"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>0%</span>
                  <span>35%</span>
                  <span>70%</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Precio compra (euros/kg)</label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="4,50"
                    value={precioCompra}
                    onChange={(e) => setPrecioCompra(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">euros/kg</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Peso neto deseado (g)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="500"
                  value={pesoNetoDeseado}
                  onChange={(e) => setPesoNetoDeseado(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Results */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Peso neto</p>
                <p className="text-2xl font-bold text-emerald-600">{fmt(pesoNeto, 0)} g</p>
                <p className="text-xs text-slate-400 mt-1">({fmt(pesoNeto / 1000, 2)} kg)</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Coste real / kg</p>
                <p className="text-2xl font-bold text-red-600">{precioRealKg > 0 ? `${fmt(precioRealKg)} euros` : '—'}</p>
                {precioNum > 0 && precioRealKg > precioNum && (
                  <p className="text-xs text-red-500 mt-1">+{fmt(((precioRealKg - precioNum) / precioNum) * 100, 0)}% vs compra</p>
                )}
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Factor rendimiento</p>
                <p className="text-2xl font-bold text-slate-800">{factorRendimiento > 0 ? fmt(factorRendimiento) : '—'}</p>
                <p className="text-xs text-slate-400 mt-1">x peso neto = bruto</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Bruto necesario</p>
                <p className="text-2xl font-bold text-slate-800">{cantidadBrutaNecesaria > 0 ? `${fmt(cantidadBrutaNecesaria, 0)} g` : '—'}</p>
                <p className="text-xs text-slate-400 mt-1">para {pesoNetoDeseado} g netos</p>
              </div>
            </div>

            {/* Alert when merma is high */}
            {merma >= 40 && precioNum > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mt-4">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Merma muy alta ({merma}%)</p>
                  <p className="text-sm text-red-700 mt-1">
                    Con esta merma, el precio real del producto utilizable es <strong>{fmt(precioRealKg)} euros/kg</strong> — un <strong>{fmt(((precioRealKg - precioNum) / precioNum) * 100, 0)}% mas</strong> que el precio de compra. Valora si te compensa comprar este ingrediente ya limpio o semielaborado.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* CTA after calculator */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-12 print:hidden">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="font-bold text-slate-900 mb-1">
                  Controla las mermas de todos tus ingredientes con PLEKO
                </p>
                <p className="text-sm text-slate-600">
                  PLEKO aplica la merma de cada ingrediente automaticamente a todos tus escandallos. Cuando cambias una merma, todos los food costs se recalculan al instante. Escanea facturas con IA, controla precios y reduce el desperdicio.
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200 flex-shrink-0"
              >
                Probar PLEKO gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ═══════════ SEO CONTENT ═══════════ */}
          <div className="prose prose-lg max-w-none">

            {/* 1. Como usar esta calculadora */}
            <h2 id="como-usar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como usar esta calculadora de mermas
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Calcular la merma y el coste real de cualquier ingrediente es instantaneo con esta herramienta. Sigue estos pasos:
            </p>
            <div className="space-y-4 mb-8">
              {[
                {
                  step: '1',
                  title: 'Selecciona un ingrediente o ajusta la merma manualmente',
                  desc: 'Elige un ingrediente del desplegable para cargar automaticamente su merma tipica, o mueve el slider para introducir un porcentaje personalizado (0-70%). Si no conoces la merma exacta de tu ingrediente, consulta nuestra tabla de mermas de alimentos.',
                },
                {
                  step: '2',
                  title: 'Introduce el peso bruto y el precio de compra',
                  desc: 'Indica cuanto producto bruto tienes (en gramos o kilos) y el precio por kilo que pagas al proveedor. En el campo "Peso neto deseado" pon la cantidad limpia que necesitas para tu receta.',
                },
                {
                  step: '3',
                  title: 'Lee los resultados al instante',
                  desc: 'La calculadora te muestra: el peso neto que obtendras tras aplicar la merma, el coste real por kilo (el precio que realmente pagas por producto utilizable), el factor de rendimiento y cuantos gramos brutos necesitas comprar para obtener la cantidad neta deseada.',
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">
                    {step}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800">{title}</p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 2. La formula de la merma */}
            <h2 id="formula-merma" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              La formula de la merma: como se calcula el coste real
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aunque la calculadora hace todos los calculos automaticamente, entender la formula detras es fundamental para cualquier profesional de hosteleria. La formula clave es:
            </p>

            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-8 text-center mb-8">
              <p className="text-sm text-emerald-600 font-medium uppercase tracking-wider mb-2">Formula del precio real con merma</p>
              <p className="text-2xl sm:text-3xl font-bold text-emerald-800 font-mono">
                Precio real = Precio compra / (1 - Merma%)
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6">
                <p className="text-emerald-800 font-semibold mb-2">Peso neto</p>
                <p className="text-center text-lg font-bold text-emerald-600 font-mono">
                  Peso neto = Peso bruto &times; (1 - Merma%)
                </p>
                <p className="text-sm text-slate-600 mt-3">
                  <strong>Ejemplo:</strong> 1 kg de patata con 15% merma = 1.000 &times; (1 - 0,15) = <strong>850 g</strong> utilizables.
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6">
                <p className="text-emerald-800 font-semibold mb-2">Factor de rendimiento</p>
                <p className="text-center text-lg font-bold text-emerald-600 font-mono">
                  Factor = 1 / (1 - Merma%)
                </p>
                <p className="text-sm text-slate-600 mt-3">
                  <strong>Ejemplo:</strong> Merma 15% &rarr; Factor = 1 / 0,85 = <strong>1,18</strong>. Para 500 g netos, compra 500 &times; 1,18 = 590 g brutos.
                </p>
              </div>
            </div>

            {/* 3. Tabla rapida de mermas */}
            <h2 id="tabla-rapida" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla rapida: mermas mas comunes en hosteleria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aqui tienes los 15 ingredientes mas habituales con su porcentaje de merma y factor de rendimiento. Datos contrastados con chefs en activo y actualizados en 2026.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-emerald-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Merma</th>
                    <th className="p-3 text-right font-medium">Factor rendimiento</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {MERMAS_RAPIDAS.map(([nombre, mermaVal, factor, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{nombre}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-emerald-700">{mermaVal}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-700">{factor}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Para una tabla completa con mas de 80 ingredientes, incluyendo merma de limpieza y merma de coccion por separado, consulta nuestra <Link to="/tabla-mermas-alimentos" className="text-emerald-600 hover:underline font-medium">tabla de mermas de alimentos completa &rarr;</Link>
            </p>

            {/* 4. Ejemplo langostinos */}
            <h2 id="ejemplo-langostinos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Por que la merma cambia el coste real de tus platos
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Veamos un ejemplo practico con <strong>langostinos</strong>, un ingrediente habitual y caro en hosteleria:
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Caso real: Langostinos en un restaurante</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Precio de compra</p>
                  <p className="text-xl font-bold text-slate-800">22,00 euros/kg</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Merma (cabeza, cascara, intestino)</p>
                  <p className="text-xl font-bold text-red-600">30%</p>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Precio REAL por kg utilizable</p>
                    <p className="text-xl font-bold text-red-600">31,43 euros/kg</p>
                    <p className="text-xs text-red-500">+43% vs precio de compra</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Factor de rendimiento</p>
                    <p className="text-xl font-bold text-slate-700">1,43</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Para 200 g de langostino limpio</p>
                    <p className="text-xl font-bold text-slate-700">Compra 286 g brutos</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Si tu <Link to="/escandallo" className="text-emerald-600 hover:underline">escandallo</Link> usa 22 euros/kg sin aplicar la merma, estas <strong>infravalorando el coste del plato en un 43%</strong>. En un plato que lleve 200 g de langostino pelado, la diferencia es de 1,89 euros por racion. Si sirves 30 raciones al dia, son <strong>56,57 euros diarios</strong> de margen perdido — mas de <strong>1.700 euros al mes</strong>.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800 mb-1">Consejo: valora comprar langostino pelado</p>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Si tu proveedor vende langostino pelado a 28 euros/kg, es mas barato que pelar los enteros (que te cuestan 31,43 euros/kg en producto utilizable). Ademas, ahorras tiempo de manipulacion y reduces el desperdicio en cocina.
                  </p>
                </div>
              </div>
            </div>

            {/* 5. Tipos de merma */}
            <h2 id="tipos-merma" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tipos de merma: limpieza, coccion y almacenamiento
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No toda la merma es igual. En cocina profesional distinguimos tres tipos principales, y cada uno afecta de forma distinta al coste de tus ingredientes:
            </p>

            <div className="space-y-4 mb-8">
              <div className="border-l-4 border-emerald-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Merma de limpieza (manipulacion en crudo)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es la perdida de peso al preparar el ingrediente antes de cocinarlo: pelado, retirada de semillas, deshuesado, despinado, limpieza de cascaras, eliminacion de partes no comestibles. Ejemplos: pelar patatas (15%), despinar merluza (45%), limpiar alcachofas (55%). Es la merma mas predecible y la que mejor puedes medir pesando antes y despues de manipular.
                </p>
              </div>

              <div className="border-l-4 border-orange-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Merma de coccion (proceso termico)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es la perdida de peso durante la coccion por evaporacion de agua y derretimiento de grasa. Varia enormemente segun el metodo: sous vide (3-8%), hervido (10-20%), asado (15-25%), braseado (20-35%). Un filete de ternera que pasa 2 minutos de mas en la plancha pierde un 5-8% mas de peso. La coccion sous vide es la que menor merma produce.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Merma de almacenamiento (deterioro)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es la perdida por deterioro del producto durante el almacenaje: deshidratacion, oxidacion, pudrefaccion parcial, caducidad. Es la merma mas dificil de controlar y la que mas varia entre establecimientos. Un buen sistema FIFO (primero en entrar, primero en salir), temperatura adecuada y rotacion de stock minimizan esta perdida. En restaurantes bien gestionados deberia estar por debajo del 2-3%.
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Las tres mermas se aplican de forma secuencial. Primero pierdes peso en la limpieza, luego en la coccion, y adicionalmente en el almacenamiento. La <strong>merma total</strong> que debes usar en tu escandallo es la combinacion de las tres. Nuestra <Link to="/tabla-mermas-alimentos" className="text-emerald-600 hover:underline">tabla de mermas de alimentos</Link> muestra la merma de limpieza y coccion por separado para mas de 80 ingredientes.
            </p>

            {/* 6. Como reducir mermas */}
            <h2 id="reducir-mermas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              5 consejos practicos para reducir mermas en tu cocina
            </h2>

            <div className="space-y-6 mb-10">
              <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">1</span>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Compra semielaborado cuando la merma sea alta</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Si la alcachofa tiene un 55% de merma y tu proveedor vende corazones limpios al doble de precio, haz numeros: el producto limpio puede salir mas barato despues de aplicar la merma (factor 2,22). Lo mismo con rape en lomos vs. rape entero, o langostinos pelados vs. enteros.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">2</span>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Aprovecha los recortes y subproductos</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Las espinas y cabezas de pescado sirven para fondos y fumets. Los recortes de carne para caldos y guisos. Los tallos de brocoli para purees. Convierte la merma en una elaboracion base que reduzca el coste de otras recetas. Un buen chef transforma el 70-80% de la merma en producto utilizable.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">3</span>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Forma a tu equipo en tecnicas de corte</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    La diferencia entre un cocinero experimentado y uno novato al limpiar un pescado puede ser de un 5-10% de merma adicional. Invierte en formacion: que todo el equipo aprenda a pelar dejando la minima piel, a despinar sin llevarse carne, y a deshuesar con precision.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">4</span>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Controla temperaturas y tiempos de coccion</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Un filete que pasa 2 minutos de mas pierde un 5-8% adicional de peso. El control preciso (especialmente con sous vide y hornos programables) reduce la merma de coccion de forma significativa. Estandariza tiempos y temperaturas para cada elaboracion.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center text-sm">5</span>
                <div>
                  <p className="font-semibold text-slate-800 mb-1">Mide y registra tus mermas reales</p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Lo que no se mide no se puede mejorar. Pesa los ingredientes antes y despues de la manipulacion durante al menos una semana al mes. Con PLEKO puedes registrar la merma real de cada ingrediente y el sistema la aplica automaticamente a todos los escandallos. <Link to="/desperdicio-alimentario-restaurante" className="text-emerald-600 hover:underline">Mas sobre control de desperdicio &rarr;</Link>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre mermas</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/tabla-mermas-alimentos', label: 'Tabla completa de mermas de alimentos' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: guia completa' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/food-cost', label: 'Food cost: que es y como calcularlo' },
                { to: '/desperdicio-alimentario-restaurante', label: 'Desperdicio alimentario en restaurantes' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-8 text-center text-white print:hidden">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Controla las mermas de todos tus ingredientes con PLEKO</h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
              PLEKO registra la merma de cada ingrediente, calcula el precio real automaticamente y recalcula todos tus escandallos al instante. Escanea facturas con IA, compara precios de mercado y reduce el desperdicio. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-emerald-700 font-bold rounded-xl hover:bg-emerald-50 transition-colors"
            >
              Empezar gratis — 14 dias sin tarjeta
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-emerald-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>

        {/* Sticky bottom CTA bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 py-3 px-4 z-50 print:hidden">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600 hidden sm:block">
              ¿Quieres controlar mermas automaticamente? <strong>Prueba PLEKO gratis 14 dias</strong>
            </p>
            <p className="text-sm text-slate-600 sm:hidden">
              <strong>Prueba PLEKO gratis</strong>
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors text-sm flex-shrink-0"
            >
              Probar gratis
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
